import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { User, Clock, CheckCircle2, Wallet, AlertTriangle, TrendingUp, Send } from "lucide-react";
import { fmtCurrency } from "@/lib/quote-helpers";
import { docLabel } from "@/lib/constants";
import { PaymentMatchingPanel } from "@/components/PaymentMatchingPanel";
import { DunningPanel } from "@/components/DunningPanel";

export const PaymentsTab = ({ payKunde, setPayKunde, payDash, openQuoteById, reminderBusy, sendReminder }) => (
            <div data-testid="payments-dashboard" className="space-y-6">
              {/* Kundenfilter */}
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-[Playfair_Display] text-2xl font-semibold text-[var(--brand-ink)]">
                  Zahlungsübersicht
                </h2>
                <div className="ml-auto flex items-center gap-2">
                  <User className="w-4 h-4 text-[var(--brand-muted)]" />
                  <select
                    value={payKunde}
                    onChange={(e) => setPayKunde(e.target.value)}
                    className="border border-[var(--brand-border)] rounded px-3 py-2 text-sm bg-white min-w-[200px]"
                    data-testid="payments-customer-filter"
                  >
                    <option value="">Alle Kunden</option>
                    {(payDash?.customers || []).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {!payDash ? (
                <div className="text-[var(--brand-muted)] text-sm">Wird geladen …</div>
              ) : (
                <>
                  {/* Summen-Karten */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { key: "open_deposits", label: "Offene Anzahlungen", icon: Clock, color: "#D97706", tid: "card-open-deposits" },
                      { key: "paid_deposits", label: "Bezahlte Anzahlungen", icon: CheckCircle2, color: "#059669", tid: "card-paid-deposits" },
                      { key: "open_rest", label: "Offene Restbeträge", icon: Wallet, color: "#2563EB", tid: "card-open-rest" },
                      { key: "overdue", label: "Überfällige Rechnungen", icon: AlertTriangle, color: "#DC2626", tid: "card-overdue" },
                    ].map((c) => {
                      const d = payDash[c.key];
                      const Icon = c.icon;
                      return (
                        <Card key={c.key} className="p-4 bg-white border-[var(--brand-border)]" data-testid={c.tid}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs uppercase tracking-wider text-[var(--brand-muted)]">{c.label}</span>
                            <Icon className="w-4 h-4" style={{ color: c.color }} />
                          </div>
                          <div className="text-2xl font-bold" style={{ color: c.color }}>
                            {fmtCurrency(d.total, "EUR")}
                          </div>
                          <div className="text-xs text-[var(--brand-muted)] mt-1">{d.count} Dokument(e)</div>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Umsatz pro Monat + letzter Zahlungseingang */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <Card className="p-5 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="revenue-by-month">
                      <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="w-4 h-4 text-[var(--brand-primary)]" />
                        <h3 className="font-semibold text-sm uppercase tracking-wider">Umsatz pro Monat</h3>
                      </div>
                      {payDash.revenue_by_month.length === 0 ? (
                        <div className="text-sm text-[var(--brand-muted)]">Noch keine Zahlungseingänge.</div>
                      ) : (
                        <div className="space-y-2">
                          {(() => {
                            const max = Math.max(...payDash.revenue_by_month.map((r) => r.amount), 1);
                            return payDash.revenue_by_month.map((r) => (
                              <div key={r.month} className="flex items-center gap-3">
                                <span className="text-xs w-16 text-[var(--brand-muted)]">{r.month}</span>
                                <div className="flex-1 bg-[var(--brand-border-light)] rounded-full h-5 overflow-hidden">
                                  <div className="h-5 rounded-full" style={{ width: `${(r.amount / max) * 100}%`, background: "var(--brand-primary)" }} />
                                </div>
                                <span className="text-sm font-medium w-24 text-right">{fmtCurrency(r.amount, "EUR")}</span>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </Card>
                    <Card className="p-5 bg-white border-[var(--brand-border)]" data-testid="last-payment">
                      <div className="flex items-center gap-2 mb-4">
                        <Clock className="w-4 h-4 text-[var(--brand-primary)]" />
                        <h3 className="font-semibold text-sm uppercase tracking-wider">Letzter Zahlungseingang</h3>
                      </div>
                      {payDash.last_payment ? (
                        <div className="space-y-1">
                          <div className="text-2xl font-bold text-[#059669]">
                            {fmtCurrency(payDash.last_payment.amount, payDash.last_payment.currency)}
                          </div>
                          <div className="text-sm">{payDash.last_payment.kunde}</div>
                          <button
                            className="text-sm text-[var(--brand-primary)] hover:underline"
                            onClick={() => openQuoteById(payDash.last_payment.id)}
                          >
                            {payDash.last_payment.nr}
                          </button>
                          <div className="text-xs text-[var(--brand-muted)]">
                            {(payDash.last_payment.when || "").slice(0, 10)} · {payDash.last_payment.pay_kind}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-[var(--brand-muted)]">Noch keine Zahlung erfasst.</div>
                      )}
                    </Card>
                  </div>

                  <PaymentMatchingPanel onOpenInvoice={openQuoteById} />
                  <DunningPanel onOpenInvoice={openQuoteById} />

                  {/* Listen */}
                  {[
                    { key: "overdue", label: "Überfällige Rechnungen", tid: "list-overdue", reminder: true },
                    { key: "open_rest", label: "Offene Restbeträge", tid: "list-open-rest", reminder: true },
                    { key: "open_deposits", label: "Offene Anzahlungen", tid: "list-open-deposits", reminder: false },
                    { key: "paid_deposits", label: "Bezahlte Anzahlungen", tid: "list-paid-deposits", reminder: false },
                  ].map((sec) => {
                    const items = payDash[sec.key].items;
                    if (!items.length) return null;
                    return (
                      <Card key={sec.key} className="p-5 bg-white border-[var(--brand-border)]" data-testid={sec.tid}>
                        <h3 className="font-semibold text-sm uppercase tracking-wider mb-3">{sec.label} ({items.length})</h3>
                        <div className="divide-y divide-[var(--brand-border-light)]">
                          {items.map((it) => (
                            <div key={it.id} className="flex items-center justify-between py-2 text-sm gap-3">
                              <div className="flex flex-col">
                                <span className="font-medium">{it.kunde}</span>
                                <span className="text-xs text-[var(--brand-muted)]">
                                  {docLabel(it.typ)} · {it.nr}
                                  {it.due_date ? ` · fällig ${it.due_date}` : ""}
                                  {it.reminder_count > 0 ? ` · ${it.reminder_count}× erinnert` : ""}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                {sec.reminder && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={reminderBusy === it.id}
                                    onClick={() => sendReminder(it.id)}
                                    className="border-[var(--brand-border)] text-xs"
                                    data-testid={`remind-${it.id}`}
                                    title="Zahlungserinnerung per E-Mail senden"
                                  >
                                    <Send className="w-3.5 h-3.5 mr-1" />
                                    {reminderBusy === it.id ? "Sende …" : "Erinnern"}
                                  </Button>
                                )}
                                <button
                                  onClick={() => openQuoteById(it.id)}
                                  className="font-semibold text-[var(--brand-primary)] hover:underline"
                                  data-testid={`pay-amount-${it.id}`}
                                  title="Rechnung öffnen"
                                >
                                  {fmtCurrency(it.amount, it.currency)}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    );
                  })}
                </>
              )}
            </div>

);
